import React, { useEffect, useState } from 'react';
import { ContentWrapper } from '../../utils/components';
import './market.css';
import {
    CheckIcon,
    ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faTachometerAlt,
    faGasPump,
    faCalendar,
    faCogs,
    faCarSide,
    faCar,
    faDoorClosed,    // voor "Deuren"
    faCogs as faCyl,  // voor "Cilinders"
    faPalette        // voor "Kleur"
} from '@fortawesome/free-solid-svg-icons'
import { Link, useParams } from 'react-router-dom';

const MarketDetail = () => {
    const [selectedImage, setSelectedImage] = useState(0);
    const [notFound, setNotFound] = useState(false);
    const [car, setCar] = useState({});
    const { id } = useParams();

    useEffect(() => {
        if (!id)
            return;
        const fetchVehicle = async () => {
            try {
                const res = await fetch(`https://www.autoservicethomas.be/api/market/${id}`);
                if (res.status === 404) {
                    setNotFound(true);
                    return;
                }

                if (!res.ok) {
                    throw new Error(`Fout bij laden voertuig: ${res.statusText}`);
                }
                const data = await res.json();
                setCar(data);
            } catch (err) {
                setNotFound(true); // fallback voor andere fouten
            }
        };

        fetchVehicle();
    }, [id]);

    const getJaNeeText = (value) => {
        return value ? "Ja" : "Neen";
    }

    const Accordion = () => {
        const [openIndex, setOpenIndex] = useState(null);

        const toggleAccordion = (index) => {
            setOpenIndex(openIndex === index ? null : index);
        };

        const filterValidRows = (rows) => {
            return rows.filter(({ value }) => value !== undefined && value !== null && value !== '');
        };

        const splitInTwoColumns = (rows) => {
            const mid = Math.ceil(rows.length / 2);
            const left = rows.slice(0, mid);
            const right = rows.slice(mid);
            return [left, right];
        };

        const algemeneRows = filterValidRows([
            { label: "Merk", value: car?.brand },
            { label: "Model", value: car?.model },
            { label: "Bouwjaar", value: car?.year },
            { label: "Kleur", value: car?.color },
            { label: "Brandstof", value: car?.fuel },
            { label: "Interieur", value: car?.interior },
            { label: "Deuren", value: car?.doors },
            { label: "Zitplaatsen", value: car?.seats },
            { label: "Carrosserie", value: car?.carBody },
            { label: "CO²", value: car?.co2 ? `${car.co2} g/km` : null },
        ]);

        const [algLinks, algRechts] = splitInTwoColumns(algemeneRows);

        const technischeRows = filterValidRows([
            { label: "Versnellingsbak", value: car?.transmission },
            { label: "Versnellingen", value: car?.gears },
            { label: "Motorinhoud", value: car?.engineSize ? `${car.engineSize} cm³` : null},
            { label: "Cilinders", value: car?.cilinders },
            {
                label: "Vermogen",
                value: car?.power ? `${car.power} kW, ${(car.power * 1.36).toFixed()} PK` : null
            },
            {
                label: "Carpass",
                value: car?.carpass ? (
                    <a href={car.carpass} target="_blank" rel="noopener noreferrer">Klik hier voor carpass</a>
                ) : null
            },
            { label: "Euronorm", value: car?.euronorm },
            { label: "Categorie", value: "Tweedehands" },
            { label: "Onderhoudsboekje", value: car?.maintenanceBook !== undefined ? getJaNeeText(car.maintenanceBook) : null },
            { label: "Niet-rokers auto", value: car?.smokeCar !== undefined ? getJaNeeText(car.smokeCar) : null },
            { label: "Ongevalvrij", value: car?.noDamage !== undefined ? getJaNeeText(car.noDamage) : null },
        ]);

        const [techLinks, techRechts] = splitInTwoColumns(technischeRows);

        // Render een tabel met data, inclusief lege rijen
        const renderTable = (rows) => (
            <table>
                <tbody>
                    {rows.map(({ label, value }, idx) => (
                        <tr key={idx} className="accordion-row">
                            <td className="accordion-label">{label}</td>
                            <td className="accordion-value">{value}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );

        return (
            <div className="accordion-container">
                {/* Algemene gegevens */}
                <div className="accordion-item">
                    <div className="accordion-header" onClick={() => toggleAccordion(0)}>
                        <h3>Algemene gegevens</h3>
                        <ChevronDownIcon className={`accordion-icon ${openIndex === 0 ? 'rotate' : ''}`} />
                    </div>
                    <div className={`accordion-body ${openIndex === 0 ? 'open' : ''}`}>
                        <div className="accordion-table" style={{ display: 'flex' }}>
                            <div className="accordion-column">
                                {renderTable(algLinks)}
                            </div>
                            <div className="accordion-column">
                                {renderTable(algRechts)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Technische gegevens */}
                <div className="accordion-item">
                    <div className="accordion-header" onClick={() => toggleAccordion(1)}>
                        <h3>Technische gegevens</h3>
                        <ChevronDownIcon className={`accordion-icon ${openIndex === 1 ? 'rotate' : ''}`} />
                    </div>
                    <div className={`accordion-body ${openIndex === 1 ? 'open' : ''}`}>
                        <div className="accordion-table" style={{ display: 'flex' }}>
                            <div className="accordion-column">
                                {renderTable(techLinks)}
                            </div>
                            <div className="accordion-column">
                                {renderTable(techRechts)}
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        );
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('nl-BE', {
            style: 'currency',
            currency: 'EUR',
        }).format(price);
    };

    const WhatsappLink = () => {
        // Huidige URL ophalen (client-side)
        const currentUrl = window.location.href;

        // Bericht met template, inclusief line breaks (\n)
        const message = `Hallo,\n\nIk heb interesse in het voertuig ${car.brand} ${car.model}, dewelke terug te vinden is via volgende link ${currentUrl}.`;

        // URL encode het bericht zodat spaties, nieuwe regels etc. veilig zijn voor URL
        const encodedMessage = encodeURIComponent(message);

        // Jouw telefoonnummer in internationaal formaat zonder +
        const phoneNumber = '32479710437';

        return (
            <a
                href={`https://wa.me/${phoneNumber}?text=${encodedMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-button-interesse whatsapp"
            >
                Chat Via Whatsapp
            </a>
        );
    }

    if (notFound) {
        return (
            <div className="section about-wrapper">
                <ContentWrapper>
                    <h2 className="title about-title">Helaas!</h2>
                    <p className="services-text">
                        Het voertuig dat je probeert te bekijken is helaas niet meer beschikbaar. Het is mogelijk dat het inmiddels verkocht is of offline werd gehaald.
                    </p>
                    <p className="services-text">Geen zorgen — we hebben nog andere interessante wagens in ons aanbod! Bovendien helpen we je graag bij het zoeken naar een voertuig op maat dat perfect bij jouw wensen past.</p>
                    <Link to="/tweedehands" style={{ marginTop: 10 }} className="cta-button">Onze wagens →</Link>
                </ContentWrapper>
            </div>
        );
    }

    const carSpecs = [
        {
            icon: faTachometerAlt,
            label: 'KM Stand',
            value: car?.mileage ? `${car.mileage} km` : null,
        },
        {
            icon: faGasPump,
            label: 'Brandstof',
            value: car?.fuel,
        },
        {
            icon: faCalendar,
            label: 'Bouwjaar',
            value: car?.year,
        },
        {
            icon: faCogs,
            label: 'Transmissie',
            value: car?.transmission,
        },
        {
            icon: faCarSide,
            label: 'Aandrijving',
            value: car?.drivetrain,
        },
        {
            icon: faCar,
            label: 'Motorinhoud',
            value: car?.engineSize ? `${car.engineSize} cm³` : null,
        },
        {
            icon: faDoorClosed,
            label: 'Deuren',
            value: car?.doors,
        },
        {
            icon: faCyl,
            label: 'Versnellingen',
            value: car?.gears,
        },
        {
            icon: faPalette,
            label: 'Kleur',
            value: car?.color,
        },
    ];

    return (
        <section className="section detail-section">
            <ContentWrapper>
                <div className="car-detail-page">
                    <div className="short-car-info">
                        <div className="image-slider">
                            <div className="slider-container" style={{ position: 'relative' }}>
                                {car?.photos?.length > 0 && (
                                    <>
                                        <img
                                            src={`https://autoservicethomas.be/uploads/vehicles/${car.photos[selectedImage]}`}
                                            alt="Car"
                                            className="car-image-big"
                                        />

                                        {/* Navigatiepijlen op mobiel */}
                                        <div className="slider-navigation">
                                            <button
                                                className="nav-arrow"
                                                onClick={() =>
                                                    setSelectedImage((prev) =>
                                                        prev === 0 ? car.photos.length - 1 : prev - 1
                                                    )
                                                }
                                            >
                                                ‹
                                            </button>
                                            <button
                                                className="nav-arrow"
                                                onClick={() =>
                                                    setSelectedImage((prev) =>
                                                        prev === car.photos.length - 1 ? 0 : prev + 1
                                                    )
                                                }
                                            >
                                                ›
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="thumbnails">
                                {car?.photos?.map((image, index) => (
                                    <img
                                        key={index}
                                        src={`https://autoservicethomas.be/uploads/vehicles/${image}`}
                                        alt="Thumbnail"
                                        className="thumbnail"
                                        onClick={() => setSelectedImage(index)}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="car-info">
                            <h1 className="car-title">{car?.title}</h1>
                            {
                                car?.status === "Beschikbaar" ? (
                                    <p className="car-price">{formatPrice(car?.price)} <span style={{ fontWeight: 600, fontSize: "1rem" }}>{car?.vatIncluded ? "Incl. BTW" : "Excl. BTW"}</span>
                                    </p>) : (
                                    <p className="car-price">{car?.status}</p>
                                )
                            }

                            <ul className="car-specs modern">
                                {carSpecs
                                    .filter(spec => spec.value !== undefined && spec.value !== null && spec.value !== '')
                                    .map((spec, index) => (
                                        <li key={index}>
                                            <div className="spec-row">
                                                <FontAwesomeIcon icon={spec.icon} className="spec-icon" />
                                                <div className="spec-info">
                                                    <span className="spec-label">{spec.label}</span>
                                                    <span className="spec-value">{spec.value}</span>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                            </ul>
                        </div>
                    </div>
                    <div className="car-description">
                        <h2>Beschrijving</h2>
                        <p style={{ whiteSpace: 'pre-line' }}>{car?.description}</p>
                    </div>
                    <hr className="about-break card-break" />
                    <div className="car-description">
                        <h2>Kenmerken</h2>
                        <ul className="car-features">
                            {car?.options?.map((feature, index) => (
                                <div key={index} className="option-item">
                                    <CheckIcon className="check-icon" />
                                    <span className="option-name">{feature}</span>
                                </div>
                            ))}
                        </ul>
                    </div>
                    <hr className="about-break card-break" />
                    <div className="car-description">
                        <h2>Specificaties</h2>
                        <Accordion />
                    </div>
                    <hr className="about-break card-break" />
                    <div className="car-description">
                        <h2>Interesse?</h2>
                        <p>Geïnteresseerd in deze wagen? Neem vrijblijvend contact met ons op!</p>
                        <div className="cta-buttons-interesse">
                            <Link to='/contact' className="cta-button-interesse mail">
                                Mail Ons
                            </Link>
                            <WhatsappLink />
                            <Link to="tel:0479710437" className="cta-button-interesse bellen">
                                Bel Ons
                            </Link>
                        </div>
                    </div>
                </div>
            </ContentWrapper>
        </section>
    );
}

export default MarketDetail;